import React, {
  useLayoutEffect,
  useRef,
  useContext,
  useEffect,
  useState,
} from "react";
import { createChart } from "lightweight-charts";
import { MarketContext } from "../../../context/MarketContext";
import styles from "./AssetChart.module.css";
import { IoCloseOutline } from "react-icons/io5";
import { LuSquarePlus } from "react-icons/lu";
import OrderPanel from "./OrderPanel";
import { TbSquareRoundedMinus, TbSquareRoundedPlus } from "react-icons/tb";
import useWindowDimensions from "../../../hooks/getWindowDimensions";
import { env } from "../../../constants/env";
import axios from "axios";
import LineChartIcon from "../../../icon/LineChartIcon";
import CandleSticksIcon from "../../../icon/CandleSticksIcon";

const AssetChart = ({ paused }) => {
  const { selected, tickers, positions, banner } = useContext(MarketContext);
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const dataBufferRef = useRef([]);
  const eventSourceRef = useRef(null);
  const candleInterval = 30; // seconds per candle

  const [showBanner, setShowBanner] = useState(false);
  const [orderDetails, setOrderDetails] = useState(true);
  const [chartType, setChartType] = useState("area"); // 'area' or 'candlestick'
  const [loading, setLoading] = useState(true); // loading spinner state

  const { width } = useWindowDimensions();
  const [chartHeight, setChartHeight] = useState(
    width > 768 ? window.innerHeight - 230 : 500
  );

  const toggleChartType = () => {
    setChartType((prev) => {
      dataBufferRef.current = []; // clear buffer to avoid old-format data
      return prev === "area" ? "candlestick" : "area";
    });
  };

  // Utility to process historical or buffered data
  const processHistoricalData = (data, type) => {
    if (type === "area") {
      return data.map((item) => ({
        time: item.time,
        value: item.value || item.close || 0,
      }));
    } else {
      const candles = {};
      data.forEach((item) => {
        const candleTime =
          Math.floor(item.time / candleInterval) * candleInterval;
        if (!candles[candleTime]) {
          candles[candleTime] = {
            time: candleTime,
            open: item.open || item.value || item.close || 0,
            high: item.high || item.value || item.close || 0,
            low: item.low || item.value || item.close || 0,
            close: item.close || item.value || 0,
          };
        } else {
          candles[candleTime].high = Math.max(
            candles[candleTime].high,
            item.high || item.value || item.close || 0
          );
          candles[candleTime].low = Math.min(
            candles[candleTime].low,
            item.low || item.value || item.close || 0
          );
          candles[candleTime].close = item.close || item.value || 0;
        }
      });
      return Object.values(candles).sort((a, b) => a.time - b.time);
    }
  };

  // Fetch initial historical data
  useEffect(() => {
    if (!selected?.symbol) return;

    const fetchHistory = async () => {
      setLoading(true); // show spinner
      try {
        const safeSymbol = selected.symbol.replace("/", "-");
        const res = await axios.get(`${env.apiUrl}/api/history/${safeSymbol}`, {
          params: { limit: 900 },
        });

        if (res.data && res.data.length > 0) {
          const processedData = processHistoricalData(res.data, chartType);
          dataBufferRef.current = processedData;

          if (seriesRef.current) {
            seriesRef.current.setData(processedData);
          }
        }
      } catch (err) {
        console.error("Failed to load historical data:", err);
      } finally {
        setLoading(false); // hide spinner
      }
    };

    fetchHistory();
  }, [selected, chartType]);

  // Re-process buffer on chart type change
  useEffect(() => {
    if (!seriesRef.current || dataBufferRef.current.length === 0) return;

    const reprocessedData = processHistoricalData(
      dataBufferRef.current,
      chartType
    );
    seriesRef.current.setData(reprocessedData);
  }, [chartType]);

  // SSE real-time updates
  useEffect(() => {
    if (!selected?.symbol || paused) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      return;
    }

    if (eventSourceRef.current) eventSourceRef.current.close();

    const safeSymbol = selected.symbol.replace("/", "-");
    const eventSource = new EventSource(
      `${env.apiUrl}/api/history/${safeSymbol}/stream`
    );
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const newData = JSON.parse(event.data);
        if (!newData || !seriesRef.current) return;

        const now = Math.floor(Date.now() / 1000);

        if (chartType === "area") {
          const newPoint = {
            time: newData.time || now,
            value: newData.value || newData.close || 0,
          };
          dataBufferRef.current.push(newPoint);
          if (dataBufferRef.current.length > 300) dataBufferRef.current.shift();
          seriesRef.current.update(newPoint);
        } else {
          const candleTime = Math.floor(now / candleInterval) * candleInterval;
          const lastCandle =
            dataBufferRef.current[dataBufferRef.current.length - 1];
          const newCandle = {
            time: candleTime,
            open: newData.open || newData.value || newData.close || 0,
            high: newData.high || newData.value || newData.close || 0,
            low: newData.low || newData.value || newData.close || 0,
            close: newData.close || newData.value || 0,
          };

          if (lastCandle && lastCandle.time === candleTime) {
            dataBufferRef.current[dataBufferRef.current.length - 1] = {
              ...lastCandle,
              high: Math.max(lastCandle.high, newCandle.high),
              low: Math.min(lastCandle.low, newCandle.low),
              close: newCandle.close,
            };
            seriesRef.current.update(
              dataBufferRef.current[dataBufferRef.current.length - 1]
            );
          } else {
            dataBufferRef.current.push(newCandle);
            if (dataBufferRef.current.length > 100)
              dataBufferRef.current.shift();
            seriesRef.current.update(newCandle);
          }
        }
      } catch (err) {
        console.error("Error processing SSE data:", err);
      }
    };

    eventSource.onerror = (err) => {
      console.error("SSE Error:", err);
      eventSource.close();
    };

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [selected, chartType, paused]);

  // Chart setup / series creation
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: chartHeight,
      layout: {
        background: { color: "#0f0f11" },
        textColor: "#c7c7c7",
        fontFamily: "General Sans, sans-serif",
      },
      grid: {
        vertLines: { color: "rgba(255, 255, 255, 0.05)" },
        horzLines: { color: "rgba(255, 255, 255, 0.05)" },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: true,
        borderColor: "#222",
        rightOffset: width > 768 ? 20 : 10,
        barSpacing: 7,
      },
      rightPriceScale: {
        borderColor: "#222",
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      handleScroll: false,
      handleScale: false,
    });

    let series;
    if (chartType === "area") {
      series = chart.addAreaSeries({
        topColor: "rgba(128, 0, 255, 0.5)",
        bottomColor: "rgba(128, 0, 255, 0.05)",
        lineColor: "#9c27b0",
        lineWidth: 2,
      });
    } else {
      series = chart.addCandlestickSeries({
        upColor: "#0A9981",
        downColor: "#F23545",
        borderVisible: false,
        wickUpColor: "#0A9981",
        wickDownColor: "#F23545",
        borderUpColor: "#0A9981",
        borderDownColor: "#F23545",
      });
    }

    chartRef.current = chart;
    seriesRef.current = series;

    if (dataBufferRef.current.length > 0) {
      const processedData = processHistoricalData(
        dataBufferRef.current,
        chartType
      );
      series.setData(processedData);
    }

    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current) {
        chart.resize(
          containerRef.current.clientWidth,
          width > 768 ? window.innerHeight - 230 : 500
        );
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
    };
  }, [chartType, chartHeight]);

  // Draw position markers
  useEffect(() => {
    if (!seriesRef.current) return;
    if (seriesRef.current._priceLines) {
      seriesRef.current._priceLines.forEach((line) =>
        seriesRef.current.removePriceLine(line)
      );
      seriesRef.current._priceLines = [];
    }

    const tickerPositions = positions.filter(
      (p) => p.symbol === selected.symbol
    );

    tickerPositions.forEach((p) => {
      const line = seriesRef.current.createPriceLine({
        price: p.entry,
        color: p.type === "buy" ? "#30970B" : "#970B0B",
        lineWidth: 1,
        lineStyle: 0,
        axisLabelVisible: true,
        title: `${p.type.toUpperCase()} $${p.entry.toFixed(5)}`,
      });

      if (!seriesRef.current._priceLines) seriesRef.current._priceLines = [];
      seriesRef.current._priceLines.push(line);
    });
  }, [positions, selected, chartType]);

  return (
    <div className={styles.chartContainer}>
      <div
        className={`${styles.chartTypeToggle} d-flex align-items-center gap-3`}
      >
        <div className="d-flex d-md-flex d-none align-items-center gap-2">
          <div className={styles.dot} />
          <p>Last updated: now</p>
        </div>
        <div className={styles.switchContainer}>
          <button
            onClick={toggleChartType}
            className={`${styles.switchButton}`}
          >
            <span
              className={`${styles.toggleBtn} ${
                chartType === "area" ? styles.active : ""
              }`}
            >
              <LineChartIcon active={chartType === "area"} />
            </span>
            <span
              className={`${styles.toggleBtn} ${
                chartType === "candlestick" ? styles.active : ""
              }`}
            >
              <CandleSticksIcon active={chartType === "candlestick"} />
            </span>
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="position-relative"
        style={{
          width: "100%",
          height: width > 768 ? `calc(100vh - 230px)` : "500px",
          borderRadius: "12px",
          overflow: "hidden",
          background: "#0f0f11",
        }}
      >
        {/* Loading Spinner */}
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(0,0,0,1)",
              zIndex: 10,
            }}
          >
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* Mobile Order Panel */}
        <div className={`${styles.orderWrapper} d-flex d-md-none`}>
          <div className={`${styles.orderContainer}`}>
            <div
              className={`${styles.closedContainer} d-flex align-items-center justify-content-between`}
            >
              {orderDetails ? <div></div> : <h4>Trade now</h4>}
              {orderDetails ? (
                <TbSquareRoundedMinus
                  size={20}
                  onClick={() => setOrderDetails(false)}
                />
              ) : (
                <TbSquareRoundedPlus
                  size={20}
                  onClick={() => setOrderDetails(true)}
                />
              )}
            </div>
            {orderDetails && <OrderPanel mobile />}
          </div>
        </div>

        {/* Banner */}
        {banner && (
          <div
            className={`${styles.successBanner} ${
              banner.type === "loss" ? styles.lossBanner : styles.winBanner
            }`}
          >
            <div
              className={`${styles.bannerHeader} ${
                banner.type === "loss" ? styles.lossHeader : styles.winHeader
              }`}
            >
              <strong>{banner.symbol}</strong>{" "}
              {banner.type === "loss" ? "Trade closed!" : "Trade successful"}
              <div
                className={`${styles.closeIcon} d-flex flex-column align-items-center justify-content-center`}
              >
                <IoCloseOutline onClick={() => setShowBanner(false)} />
              </div>
            </div>
            <div className={styles.bannerBody}>
              {banner.type === "loss" ? (
                <span>No payout this time</span>
              ) : (
                <>
                  <span>Total payout: {banner.payout} $PROP</span>
                  <span>Total profit: {banner.profit} $PROP</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetChart;
